import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL, GOOGLE_CLIENT_ID } from "../config";
import { useAppContext } from "../context/AppContext";

export default function Login() {
  const { login } = useAppContext();
  const googleBtnRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    remember: false,
  });
  const [step, setStep] = useState(1);

  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, type: "success", message: "" });
    }, 3000);
  };

  useEffect(() => {
    const hasToken =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const hasUser = localStorage.getItem("user");

    if (hasToken && hasUser && hasUser !== "undefined") {
      navigate(from);
    }

    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail)
      setFormData((prev) => ({ ...prev, email: savedEmail, remember: true }));
  }, []);

  useEffect(() => {
    const loadGoogleScript = () => {
      if (
        document.querySelector(
          'script[src="https://accounts.google.com/gsi/client"]',
        )
      ) {
        initializeGoogle();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    };

    const initializeGoogle = () => {
      if (!window.google || !googleBtnRef.current) return;

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });

      googleBtnRef.current.innerHTML = "";

      // Determine responsive width for Google button
      const screenWidth = window.innerWidth;
      const btnWidth =
        screenWidth < 450
          ? Math.max(200, Math.min(screenWidth - 64, 370))
          : 370;

      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        text: "continue_with",
        shape: "rectangular",
        width: btnWidth,
      });
    };

    loadGoogleScript();

    window.addEventListener("resize", initializeGoogle);
    return () => window.removeEventListener("resize", initializeGoogle);
  }, []);

  const handleGoogleResponse = async (response) => {
    try {
      setLoading(true);

      const res = await axios.post(
        `${API_BASE_URL}/api/auth/google`,
        { credential: response.credential },
        { headers: { "Content-Type": "application/json" } },
      );

      const { token, user } = res.data;

      await login(token, user, formData.remember);

      showToast("Login successful", "success");

      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1200);
    } catch (error) {
      showToast(
        error.response?.data?.message || "Google login failed",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await axios.post(`${API_BASE_URL}/api/auth/send-otp`, {
        email: formData.email,
      });

      showToast("OTP sent to your email", "success");
      setOtpSent(true); // Set OTP sent status
      setStep(2);
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to send OTP",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    if (!formData.otp) {
      setErrors({ otp: "Please enter the OTP" });
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-otp`, {
        email: formData.email,
        otp: formData.otp,
      });

      const { token, user } = response.data;

      await login(token, user, formData.remember);

      showToast("Login successful", "success");

      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1000);
    } catch (error) {
      showToast(error.response?.data?.message || "Invalid OTP", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-container d-flex justify-content-center align-items-center min-vh-100 px-3 py-4">
        <div
          className="login-card p-3 p-sm-4 shadow rounded bg-white"
          style={{ width: "100%", maxWidth: "400px" }}
        >
          <div className="logo-container text-center mb-4">
            <img
              src="/assets/img/a1-img/Logo.png"
              alt="logo"
              style={{ maxWidth: "120px", margin: "0 auto" }}
            />
            <div className="login-header mt-3">
              <h1 className="h3 mb-2">Welcome back</h1>
              <p className="text-muted">
                Please enter your details to sign in.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <div
              ref={googleBtnRef}
              className="d-flex justify-content-center w-100 overflow-hidden"
            />
          </div>

          <div className="divider text-center my-3">
            <span className="text-muted bg-white px-2">or</span>
          </div>

          <form onSubmit={step === 1 ? handleSendOTP : handleVerifyOTP}>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <input
                type="email"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                disabled={otpSent} // Disable email field after OTP is sent
                placeholder="name@company.com"
              />
              {errors.email && (
                <div className="invalid-feedback">{errors.email}</div>
              )}
            </div>

            {step === 2 && (
              <div className="mb-3">
                <label htmlFor="otp" className="form-label">
                  Enter OTP
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.otp ? "is-invalid" : ""}`}
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleChange}
                  placeholder="123456"
                />
                {errors.otp && (
                  <div className="invalid-feedback">{errors.otp}</div>
                )}
              </div>
            )}

            <div className="mb-4 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="remember"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="remember">
                Remember me
              </label>
            </div>

            <button
              type="submit"
              className="bm-hero__cta w-100"
              disabled={loading}
            >
              {loading
                ? "Processing..."
                : step === 1
                  ? "Login"
                  : "Verify & Login"}
            </button>
          </form>
        </div>
      </div>

      {toast.show && (
        <div
          className={`position-fixed top-0 end-0 mt-5 me-3 p-3 text-white rounded shadow-lg ${
            toast.type === "success" ? "bg-success" : "bg-danger"
          }`}
          style={{ zIndex: 1050 }}
        >
          {toast.message}
        </div>
      )}
    </>
  );
}
